/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: RoleProfilesActions.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Action buttons component for Role Profiles DataGrid rows.
────────────────────────────────────────────────────────────────*/

import { Edit, Copy, Trash2 } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { RoleProfile } from '../types';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';

interface RoleProfilesActionsProps {
  profile: RoleProfile;
  onEdit: (profile: RoleProfile) => void;
  onDuplicate: (profile: RoleProfile) => void;
  onDelete: (profile: RoleProfile) => void;
}

export function RoleProfilesActions({
  profile,
  onEdit,
  onDuplicate,
  onDelete
}: RoleProfilesActionsProps) {
  const { t } = useI18n();

  return (
    <div className="flex items-center gap-1.5">
      <SimpleTooltip content={t('profiles.actions.edit')} position="top">
        <button
          type="button"
          onClick={() => onEdit(profile)}
          className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded transition-colors"
        >
          <Edit className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('profiles.actions.duplicate')} position="top">
        <button
          type="button"
          onClick={() => onDuplicate(profile)}
          className="p-2 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 rounded transition-colors"
        >
          <Copy className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip
        content={profile.is_system ? t('profiles.messages.cannotDeleteSystem') : t('profiles.actions.delete')}
        position="top"
      >
        <button
          type="button"
          onClick={() => onDelete(profile)}
          disabled={profile.is_system}
          className={`p-2 rounded transition-colors ${profile.is_system
              ? 'text-gray-400 opacity-50 cursor-not-allowed'
              : 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
            }`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </SimpleTooltip>
    </div>
  );
}
